// Cart state context for the marketplace embed.
//
// Uses useReducer for state-machine style optimistic updates —
// mirrors the forms embed pattern of context + reducer (NOT React
// Query) to keep the embed bundle lean.

import {
  createContext,
  useContext,
  useReducer,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  type ReactNode,
} from "react";
import { embedApiFetch, ApiError } from "./api-client";
import {
  idempotencyHeaders,
  idempotencyUserMessage,
  isIdempotencyCodeBug,
} from "./idempotency-errors";
import { useAuth } from "./auth-context";
import {
  readGuestCart,
  writeGuestCart,
  addGuestEntry,
  clearGuestCart,
  MAX_GUEST_QUANTITY,
  type GuestCartEntry,
} from "./guest-cart";
import type { Cart, CartLine } from "./types";


// -- Guest staging helpers ---------------------------------------------------

/**
 * Synthetic line id for a staged (not-yet-persisted) guest line. Guest lines
 * have no server row, but the CartLine contract requires an id and consumers
 * key React lists on it. Prefixing keeps guest ids unmistakable and lets the
 * guest mutation paths recover the product id without parsing — product ids
 * may themselves contain dashes, so `slice` on a known prefix is used rather
 * than splitting.
 */
const GUEST_LINE_ID_PREFIX = "guest-";

function guestLineId(productId: string): string {
  return `${GUEST_LINE_ID_PREFIX}${productId}`;
}

/** Product id behind a guest line id, or null if this isn't a guest line. */
function guestProductId(lineId: string): string | null {
  if (!lineId.startsWith(GUEST_LINE_ID_PREFIX)) return null;
  const productId = lineId.slice(GUEST_LINE_ID_PREFIX.length);
  return productId.length > 0 ? productId : null;
}

/**
 * Merge duplicate product ids into one entry, summing quantity and clamping to
 * the buffer's own ceiling — i.e. exactly what `addGuestEntry` would have
 * produced had both adds gone through it.
 *
 * `readGuestCart` does NOT dedupe and `writeGuestCart` is public, so a buffer
 * like `[{p1,1},{p1,2}]` is persistable, and `addGuestEntry`'s `.find()` only
 * ever increments the FIRST match — a duplicate, once present, is permanent.
 * Every consumer here collapses first, which buys three things:
 *   - replay issues ONE POST per product, so the wire shape stays honest;
 *   - the rendered line list has unique React keys;
 *   - `updateLineQty` (map/first-match) and `removeLine` (filter/all-matches)
 *     cannot disagree, because at most one entry per product ever survives.
 */
function collapseGuestEntries(entries: GuestCartEntry[]): GuestCartEntry[] {
  const byProduct = new Map<string, GuestCartEntry>();
  for (const entry of entries) {
    const existing = byProduct.get(entry.product_id);
    if (existing) {
      existing.quantity = Math.min(existing.quantity + entry.quantity, MAX_GUEST_QUANTITY);
    } else {
      byProduct.set(entry.product_id, { ...entry });
    }
  }
  // Map preserves insertion order, so replay order stays deterministic.
  return [...byProduct.values()];
}

/** The staged buffer, normalised. The only way guest state should be read. */
function readStagedEntries(): GuestCartEntry[] {
  return collapseGuestEntries(readGuestCart());
}

// -- State machine -----------------------------------------------------------

type CartStatus = "idle" | "loading" | "ready" | "error";

interface CartState {
  status: CartStatus;
  cart: Cart | null;
  error: string | null;
}

type CartAction =
  | { type: "FETCH_START" }
  | { type: "FETCH_SUCCESS"; cart: Cart }
  | { type: "FETCH_ERROR"; error: string }
  | { type: "OPTIMISTIC_ADD_LINE"; line: CartLine }
  | { type: "OPTIMISTIC_UPDATE_QTY"; lineId: string; quantity: number }
  | { type: "OPTIMISTIC_REMOVE_LINE"; lineId: string }
  | { type: "SET_CART"; cart: Cart }
  | { type: "SET_GUEST_LINES"; entries: GuestCartEntry[] }
  | { type: "CLEAR" };

function cartReducer(state: CartState, action: CartAction): CartState {
  switch (action.type) {
    case "FETCH_START":
      return { ...state, status: "loading", error: null };
    case "FETCH_SUCCESS":
      return { status: "ready", cart: action.cart, error: null };
    case "FETCH_ERROR":
      return { ...state, status: "error", error: action.error };
    // Optimistic total recomputes are approximations — tax, promo, and
    // shipping are carried forward from the last server-reconciled state
    // and may be transiently stale. The SET_CART action (fired when the
    // server responds) overwrites these with the authoritative totals.
    case "OPTIMISTIC_ADD_LINE": {
      if (!state.cart) return state;
      const lines = [...state.cart.lines, action.line];
      return {
        ...state,
        cart: {
          ...state.cart,
          lines,
          subtotal: lines.reduce((s, l) => s + l.line_total, 0),
          total: lines.reduce((s, l) => s + l.line_total, 0) +
            state.cart.tax_estimate + state.cart.shipping - state.cart.promo_discount,
        },
      };
    }
    case "OPTIMISTIC_UPDATE_QTY": {
      if (!state.cart) return state;
      // C2 Task 2 (TD-518 SPA half): on a "whole_line" (device-priced) line the
      // whole-line price for the NEW device count is unknowable client-side
      // (unit_price is already whole-line for the OLD count), so keep the
      // existing line_total until the server response (SET_CART) replaces it
      // rather than flashing unit_price * quantity (the old double multiply).
      // "per_unit" and absent price_basis keep the multiply.
      const lines = state.cart.lines.map((l) =>
        l.id === action.lineId
          ? {
              ...l,
              quantity: action.quantity,
              line_total:
                l.price_basis === "whole_line" ? l.line_total : l.unit_price * action.quantity,
            }
          : l,
      );
      return {
        ...state,
        cart: {
          ...state.cart,
          lines,
          subtotal: lines.reduce((s, l) => s + l.line_total, 0),
          total: lines.reduce((s, l) => s + l.line_total, 0) +
            state.cart.tax_estimate + state.cart.shipping - state.cart.promo_discount,
        },
      };
    }
    case "OPTIMISTIC_REMOVE_LINE": {
      if (!state.cart) return state;
      const lines = state.cart.lines.filter((l) => l.id !== action.lineId);
      return {
        ...state,
        cart: {
          ...state.cart,
          lines,
          subtotal: lines.reduce((s, l) => s + l.line_total, 0),
          total: lines.reduce((s, l) => s + l.line_total, 0) +
            state.cart.tax_estimate + state.cart.shipping - state.cart.promo_discount,
        },
      };
    }
    case "SET_CART":
      return { status: "ready", cart: action.cart, error: null };
    // Project the staged guest buffer into the cart shape. Money fields stay
    // ZERO — deliberately. The buffer holds only product ids and quantities
    // (spec §6.1), and prices are server-authoritative, so the guest cart view
    // labels them "Pricing calculated at checkout" rather than showing a
    // locally invented number that could disagree with checkout.
    case "SET_GUEST_LINES": {
      const lines: CartLine[] = action.entries.map((e) => ({
        id: guestLineId(e.product_id),
        product_id: e.product_id,
        product_name: "",
        product_slug: "",
        quantity: e.quantity,
        unit_price: 0,
        line_total: 0,
        currency: "USD",
      }));
      return { status: "ready", cart: { ...EMPTY_CART, lines }, error: null };
    }
    case "CLEAR":
      // CLEAR means "the cart is now empty" (checkout completion, Task 17
      // review round 2 — CheckoutFlow.tsx's post-order clearCart() call;
      // sign-out, MarketplaceEmbed.tsx's onLogout clearToken()+clearCart()
      // pair, which also reaches this via the token-change effect's
      // `wasSignedIn` branch), NOT "unknown". Returning "ready" with
      // EMPTY_CART — exactly what SET_GUEST_LINES([]) already produces —
      // means both call sites land on a real, renderable empty cart instead
      // of "idle", which CartView (after review round 1) can no longer
      // treat as "safe to show empty" because "idle" is also
      // INITIAL_STATE. A single producer-side fix covers both call sites at
      // once, matching the reasoning that closed round 1.
      // Return a FRESH copy (TD-314), not EMPTY_CART by identity. SET_GUEST_LINES
      // already spreads ({ ...EMPTY_CART, lines }), but CLEAR returned the
      // module-level constant directly — a latent shared-constant-corruption
      // bug if anything ever mutated state.cart (or state.cart.lines) in place,
      // since every CLEAR consumer would then share the mutated object.
      return { status: "ready", cart: { ...EMPTY_CART, lines: [] }, error: null };
    default:
      return state;
  }
}

const EMPTY_CART: Cart = {
  id: "",
  lines: [],
  subtotal: 0,
  tax_estimate: 0,
  promo_discount: 0,
  shipping: 0,
  total: 0,
  promo_code: null,
};

const INITIAL_STATE: CartState = {
  status: "idle",
  cart: null,
  error: null,
};

// -- Context -----------------------------------------------------------------

/**
 * Explicit outcome of an addLine() / updateLineQty() / removeLine() call
 * (Task 15 fix, extended to updateLineQty/removeLine in Task 17). Each of
 * these dispatches to `state` exactly as it always has — this return value
 * is ADDITIONAL, not a replacement. It exists because a caller that needs to
 * render immediate UI feedback (e.g. ProductDetailView's Add to Cart
 * confirmation/alert, CartView's per-line error message) cannot safely infer
 * the outcome by re-reading `state` after the call resolves: the dispatch
 * and the awaiting caller's own continuation are not ordered relative to
 * each other in general. The authenticated branch's dispatch happens inside
 * a promise continuation and is scheduled through React's Scheduler (a
 * macrotask), which runs AFTER the microtask that resumes the caller's
 * `await` — so a caller reading `state.error` right after `await
 * addLine(...)` sees whatever was there BEFORE this call, not the result of
 * this call. Worse, for updateLineQty/removeLine specifically: a failed
 * PATCH/DELETE whose rollback GET succeeds dispatches FETCH_SUCCESS, which
 * *clears* `state.error` entirely (it lands with `error: null`) — so even a
 * caller willing to accept the ordering race would read a false "no error"
 * for a call that genuinely failed. (The guest branch's dispatch happens
 * synchronously before any `await`, so it does NOT have the ordering problem
 * today — but that synchronicity is an implementation detail of
 * sessionStorage access being synchronous, not a guaranteed contract, and a
 * caller relying on it would silently break the moment that branch ever
 * gains an `await`.) An explicit return value sidesteps both problems
 * entirely: it is produced and returned from the exact code path that
 * decided the outcome, independent of whatever `state` ends up holding.
 */
export interface AddLineResult {
  ok: boolean;
  error?: string;
}

/**
 * Optional extras for addLine (device-count unit, Task 9). `storeId` rides
 * the Task-7 cart-add wire: an OPTIONAL, nullable association validated
 * server-side (a foreign store is the same 404 as a nonexistent one).
 * GUEST adds ignore it — the guest buffer holds ids+quantities only, and the
 * signed-out store flow STAGES a draft via store-staging.ts instead (design
 * §6.2 ruling 9(a)); the association is reconciled after login.
 *
 * `newLine` (Task 10, design §5) carries the add-again prompt's explicit
 * "new subscription" intent: a separate line, never a merge into an existing
 * same-product line. Line identity is CLIENT-mediated — the server cannot
 * distinguish "more devices" from "another subscription at another store"
 * from a bare add (it merges same-product adds unconditionally), so the
 * choice must travel in the request. The increment arm of that choice never
 * reaches addLine at all — it is a line-update PATCH on the named line.
 * ⚠️ AMENDED (device-count unit close-out, rule 104) — ORIGINAL: "TD-456
 * (design §4): the Go cart-add handler does not read `new_line` yet at the
 * time this sender landed — the reader is the Go-lane half of design §5;
 * until it lands this flag is inert server-side and the SPA's add-again
 * tests mock the honoring behavior. Nothing deploys from the branch
 * before both halves land (unit Ruling 5)." FALSIFIED — the Go reader
 * landed (cart-add honors `new_line` and inserts a second line; a bare
 * add into an already-multi-line same-product cart is rejected 422
 * AMBIGUOUS_LINE_TARGET), so this flag is LIVE server-side, pinned by
 * Go-side tests (`cart_mutation_newline_test.go`,
 * `cart_mutation_bare_add_ambiguity_test.go`). GUEST adds ignore it for
 * the same structural reason as `storeId`: the guest buffer accumulates per
 * product (one staged line per product), so a second staged line is not
 * representable client-side — the PDP's guest add-again flow routes the
 * new-subscription choice to sign-in rather than staging a second line that
 * replay would silently merge (the exact failure design §5 prohibits).
 */
export interface AddLineOptions {
  storeId?: string;
  newLine?: boolean;
}

// Exported (type-only consumers) so tests can construct a well-typed stub
// value when mocking useCart() directly — e.g. CartView.idle-state.test.tsx,
// which pins a CartView-level rendering bug against a specific CartState
// shape independent of CartProvider's real effect timing (Task 17 review
// round 1). No runtime behavior change.
export interface CartContextValue {
  state: CartState;
  /**
   * True while there is no customer JWT. In guest mode `addLine`,
   * `updateLineQty` and `removeLine` operate on the sessionStorage staging
   * buffer instead of the bearer-protected `/me/cart*` API, and all money
   * fields are zero. `applyPromo` / `clearPromo` remain server-only — consumers
   * hide the promo input rather than showing it broken (spec §6.2).
   */
  isGuest: boolean;
  /** Number of items in the cart (sum of quantities). */
  itemCount: number;
  /** Fetch cart from server. */
  fetchCart: () => Promise<void>;
  /** Add a product to the cart. Resolves with the explicit outcome — see
   *  AddLineResult; callers rendering immediate feedback MUST read it rather
   *  than infer success/failure from `state` after the fact. */
  addLine: (productId: string, quantity: number, opts?: AddLineOptions) => Promise<AddLineResult>;
  /** Update a line item's quantity. Resolves with the explicit outcome —
   *  see AddLineResult; callers rendering per-action feedback MUST read it
   *  rather than infer success/failure from `state` after the fact (Task
   *  17 fix — see AddLineResult's doc comment for why `state.error` is
   *  unreliable here specifically). */
  updateLineQty: (lineId: string, quantity: number) => Promise<AddLineResult>;
  /** Remove a line item. Resolves with the explicit outcome — see
   *  updateLineQty's doc comment; the same rationale applies. */
  removeLine: (lineId: string) => Promise<AddLineResult>;
  /**
   * Associate (or reassociate) a line with one of the customer's stores
   * (device-count unit, Task 9): PATCH /me/cart/lines/{id} with
   * {quantity, store_id}. The wire contract requires `quantity` ALONGSIDE
   * store_id on every line PATCH (api-documentation.md, Task 7) — the caller
   * supplies the line's current quantity because it renders the line and the
   * context deliberately keeps its callbacks identity-stable (deps
   * [token]-only) rather than closing over live cart state. Reassigning is
   * legal (Ruling 10's PATCH semantics); explicit-null CLEARING is not
   * offered — no valid customer flow removes a store from a recurring line
   * and proceeds, since checkout would then refuse the cart. Resolves with
   * the explicit outcome (AddLineResult) — same rationale as updateLineQty.
   */
  setLineStore: (lineId: string, storeId: string, quantity: number) => Promise<AddLineResult>;
  /** Apply a promo code. Returns the updated cart. */
  applyPromo: (code: string) => Promise<Cart>;
  /** Clear promo code. */
  clearPromo: () => Promise<void>;
  /** Clear local cart state (after checkout or logout). */
  clearCart: () => void;
}

const CartContext = createContext<CartContextValue | null>(null);

export function CartProvider({ children }: { children: ReactNode }) {
  const { token } = useAuth();
  const [state, dispatch] = useReducer(cartReducer, INITIAL_STATE);

  // Request-generation guard (TD-314). Each fetchCart call takes a generation
  // number; CLEAR bumps it. On resolve, a fetch dispatches FETCH_SUCCESS / _
  // ERROR only if its generation still matches — so a fetch that was in flight
  // when a sign-out / checkout CLEAR landed cannot resurrect the just-cleared
  // cart with stale data. fetchCart also self-supersedes: a newer fetch wins.
  const fetchGenRef = useRef(0);

  const fetchCart = useCallback(async () => {
    const gen = ++fetchGenRef.current;
    dispatch({ type: "FETCH_START" });
    try {
      const cart = await embedApiFetch<Cart>("/api/v1/marketplace/me/cart");
      if (gen === fetchGenRef.current) dispatch({ type: "FETCH_SUCCESS", cart: cart ?? EMPTY_CART });
    } catch (err) {
      if (gen === fetchGenRef.current) {
        const msg = err instanceof ApiError ? err.message : "Failed to load cart";
        dispatch({ type: "FETCH_ERROR", error: msg });
      }
    }
  }, []);

  const addLine = useCallback(async (productId: string, quantity: number, opts?: AddLineOptions): Promise<AddLineResult> => {
    // Reject a non-positive / non-integer quantity up front with the CORRECT
    // cause (TD-310). Without this guard, a guest addLine(productId, 0) falls
    // through to addGuestEntry — which rejects 0 by returning the buffer
    // unchanged — and the before/after quantity check below reports the GENERIC
    // staging-failure copy ("We couldn't add that item... sign in"), wrong about
    // the cause and unhelpfully suggesting a remedy (sign in) that does nothing
    // for an invalid quantity. The authenticated branch would otherwise POST
    // quantity 0 to the server. Validating once here covers both branches and
    // mirrors addGuestEntry's own contract (positive integer or reject).
    if (!Number.isInteger(quantity) || quantity <= 0) {
      const error = "Please choose a quantity of at least 1.";
      dispatch({ type: "FETCH_ERROR", error });
      return { ok: false, error };
    }
    if (!token) {
      // Signed out → stage locally. No API call: /me/cart/lines is
      // bearer-protected, so an unauthenticated POST 401s and the rollback GET
      // 401s too, making the primary CTA a silent no-op. addGuestEntry rejects
      // bad arguments by returning the buffer unchanged.
      // writeGuestCart swallows storage failures (sandboxed iframe, private
      // mode, quota), so addGuestEntry hands back the UNCHANGED buffer and the
      // add evaporates. Reporting success there would be a silent no-op — the
      // exact defect this task exists to remove (spec §6.4) — and worse than
      // the pre-guest-mode behaviour, which at least surfaced the 401.
      //
      // Branch on the quantity having INCREASED, not on the product being
      // present. On a REPEAT add, addGuestEntry mutates its own local copy and
      // then returns readGuestCart() — the stale STORED buffer, which still
      // contains productId even though nothing persisted. A presence check
      // would see it and report success with the old quantity.
      const stagedQuantity = (list: GuestCartEntry[]): number =>
        list.find((e) => e.product_id === productId)?.quantity ?? 0;
      const before = stagedQuantity(readStagedEntries());
      const entries = collapseGuestEntries(addGuestEntry(productId, quantity));
      if (stagedQuantity(entries) <= before) {
        // Task 15 residual: when the product was ALREADY at the per-product
        // ceiling, the outcome claim ("couldn't add") is still true, but the
        // generic copy is wrong about the cause and unhelpfully suggests
        // retrying or signing in — neither raises the ceiling. Reference the
        // exported constant (never a hardcoded 99 — project rule §55) so this
        // can't silently drift from guest-cart.ts's own ceiling.
        const error =
          before === MAX_GUEST_QUANTITY
            ? `You've reached the maximum quantity (${MAX_GUEST_QUANTITY}) for this item.`
            : "We couldn't add that item to your cart. Try again, or sign in to save your cart to your account.";
        dispatch({ type: "FETCH_ERROR", error });
        return { ok: false, error };
      }
      // Re-persist the COLLAPSED buffer so storage never retains duplicates
      // (TD-310). addGuestEntry writes an UN-collapsed array back (readGuestCart
      // does not dedupe and writeGuestCart is public), so without this write a
      // pre-existing duplicate survives in storage and addGuestEntry's
      // `.find()` only ever increments the FIRST match — a duplicate, once
      // present, is otherwise permanent. updateLineQty/removeLine already write
      // collapsed arrays (they build `next` from readStagedEntries); this makes
      // addLine consistent so all three mutation paths reconcile storage.
      writeGuestCart(entries);
      dispatch({ type: "SET_GUEST_LINES", entries });
      return { ok: true };
    }
    // Optimistic: add a placeholder line. Server response replaces it.
    const tempLine: CartLine = {
      id: `temp-${Date.now()}`,
      product_id: productId,
      product_name: "",
      product_slug: "",
      quantity,
      unit_price: 0,
      line_total: 0,
    };
    dispatch({ type: "OPTIMISTIC_ADD_LINE", line: tempLine });
    try {
      const cart = await embedApiFetch<Cart>("/api/v1/marketplace/me/cart/lines", {
        method: "POST",
        // Task-7 wire: store_id is included ONLY when the caller chose one —
        // an absent key is the "no association" case, distinct from null.
        // Task 10 (design §5): new_line is included ONLY when the caller
        // carries the add-again prompt's explicit "new subscription" intent.
        body: {
          product_id: productId,
          quantity,
          ...(opts?.storeId ? { store_id: opts.storeId } : {}),
          ...(opts?.newLine ? { new_line: true } : {}),
        },
        headers: idempotencyHeaders(),
      });
      if (cart) dispatch({ type: "SET_CART", cart });
      return { ok: true };
    } catch (err) {
      // Surface idempotency-specific user-friendly message when available.
      if (err instanceof ApiError) {
        if (isIdempotencyCodeBug(err.code)) {
          console.warn("[cart] missing Idempotency-Key — code bug", err.code);
        }
        const userMsg = idempotencyUserMessage(err.code);
        if (userMsg) {
          dispatch({ type: "FETCH_ERROR", error: userMsg });
          return { ok: false, error: userMsg };
        }
      }
      // Rollback: re-fetch the real cart. The add itself failed either way —
      // the returned outcome below reports THAT failure regardless of
      // whether this resync fetch succeeds or fails.
      const msg = err instanceof ApiError ? err.message : "Failed to add item";
      try {
        const cart = await embedApiFetch<Cart>("/api/v1/marketplace/me/cart");
        dispatch({ type: "FETCH_SUCCESS", cart: cart ?? EMPTY_CART });
      } catch {
        dispatch({ type: "FETCH_ERROR", error: msg });
      }
      return { ok: false, error: msg };
    }
  }, [token]);

  const updateLineQty = useCallback(async (lineId: string, quantity: number): Promise<AddLineResult> => {
    // Bad input rejected up front for BOTH branches (TD-310): previously only
    // the guest branch validated, so an authenticated updateLineQty(id, NaN)
    // would dispatch + PATCH a nonsensical quantity. Falling through to
    // writeGuestCart's isValidEntry filter would silently DELETE the line on
    // the guest side for NaN / non-integer / negative input instead of leaving
    // it untouched.
    if (!Number.isInteger(quantity) || quantity < 0) return { ok: false };
    if (!token) {
      const productId = guestProductId(lineId);
      // Defensive — shouldn't happen from real UI (every guest line id is
      // guest-prefixed). No dispatch, matching the pre-Task-17 no-op exactly;
      // just an honest return value on top.
      if (!productId) return { ok: false };
      const staged = readStagedEntries();
      // Quantity 0 means "remove this line" — the conventional cart-stepper
      // semantic, and made explicit here rather than inherited from the
      // isValidEntry drop.
      const expectedQuantity = quantity === 0 ? 0 : Math.min(quantity, MAX_GUEST_QUANTITY);
      const next =
        quantity === 0
          ? staged.filter((e) => e.product_id !== productId)
          : staged.map((e) =>
              e.product_id === productId
                ? { ...e, quantity: expectedQuantity }
                : e,
            );
      writeGuestCart(next);
      // Read back what actually persisted — writeGuestCart swallows storage
      // failures (quota, disabled storage), so a failed write silently
      // leaves the OLD buffer in place. Comparing the read-back quantity
      // against what we intended to write is the only way to notice (Task
      // 17 / live-defect fix — mirrors addLine's before/after quantity
      // check above).
      const after = readStagedEntries();
      dispatch({ type: "SET_GUEST_LINES", entries: after });
      const actualQuantity = after.find((e) => e.product_id === productId)?.quantity ?? 0;
      if (actualQuantity !== expectedQuantity) {
        const error =
          "We couldn't update your cart. Try again, or sign in to save your cart to your account.";
        dispatch({ type: "FETCH_ERROR", error });
        return { ok: false, error };
      }
      return { ok: true };
    }
    // quantity === 0 means "remove this line" (TD-310) — the conventional
    // cart-stepper semantic the guest branch above already implements. The
    // authenticated branch previously PATCHed {quantity: 0}, DIVERGING from the
    // guest branch's remove and relying on undocumented server-side 0-handling
    // (a PATCH-0 that the server does not map to a remove would leave an
    // invisible 0-qty line). DELETE here makes both modes agree that 0 = remove,
    // independent of server PATCH-0 semantics, and reuses removeLine's rollback
    // shape (re-fetch on failure).
    if (quantity === 0) {
      dispatch({ type: "OPTIMISTIC_REMOVE_LINE", lineId });
      try {
        const cart = await embedApiFetch<Cart>(
          `/api/v1/marketplace/me/cart/lines/${lineId}`,
          { method: "DELETE" },
        );
        if (cart) dispatch({ type: "SET_CART", cart });
        return { ok: true };
      } catch (err) {
        const msg = err instanceof ApiError ? err.message : "Failed to remove item";
        try {
          const cart = await embedApiFetch<Cart>("/api/v1/marketplace/me/cart");
          dispatch({ type: "FETCH_SUCCESS", cart: cart ?? EMPTY_CART });
        } catch {
          dispatch({ type: "FETCH_ERROR", error: msg });
        }
        return { ok: false, error: msg };
      }
    }
    dispatch({ type: "OPTIMISTIC_UPDATE_QTY", lineId, quantity });
    try {
      const cart = await embedApiFetch<Cart>(
        `/api/v1/marketplace/me/cart/lines/${lineId}`,
        { method: "PATCH", body: { quantity }, headers: idempotencyHeaders() },
      );
      if (cart) dispatch({ type: "SET_CART", cart });
      return { ok: true };
    } catch (err) {
      // Surface idempotency-specific user-friendly message when available.
      if (err instanceof ApiError) {
        if (isIdempotencyCodeBug(err.code)) {
          console.warn("[cart] missing Idempotency-Key — code bug", err.code);
        }
        const userMsg = idempotencyUserMessage(err.code);
        if (userMsg) {
          dispatch({ type: "FETCH_ERROR", error: userMsg });
          return { ok: false, error: userMsg };
        }
      }
      // Rollback: re-fetch the real cart. The update itself failed either
      // way — the returned outcome below reports THAT failure regardless of
      // whether this resync fetch succeeds or fails. (Task 17 / live-defect
      // fix: on a successful resync the dispatch below is FETCH_SUCCESS,
      // which clears state.error — this explicit return value is the only
      // signal a caller can rely on; see AddLineResult's doc comment.)
      const msg = err instanceof ApiError ? err.message : "Failed to update item";
      try {
        const cart = await embedApiFetch<Cart>("/api/v1/marketplace/me/cart");
        dispatch({ type: "FETCH_SUCCESS", cart: cart ?? EMPTY_CART });
      } catch {
        dispatch({ type: "FETCH_ERROR", error: msg });
      }
      return { ok: false, error: msg };
    }
  }, [token]);

  const removeLine = useCallback(async (lineId: string): Promise<AddLineResult> => {
    if (!token) {
      const productId = guestProductId(lineId);
      if (!productId) return { ok: false };
      writeGuestCart(readStagedEntries().filter((e) => e.product_id !== productId));
      // Same read-back check as updateLineQty above: writeGuestCart swallows
      // storage failures, so a failed write silently leaves the line in
      // place. Checking whether it's still present is the only way to
      // notice (Task 17 / live-defect fix).
      const after = readStagedEntries();
      dispatch({ type: "SET_GUEST_LINES", entries: after });
      const stillPresent = after.some((e) => e.product_id === productId);
      if (stillPresent) {
        const error =
          "We couldn't remove that item. Try again, or sign in to save your cart to your account.";
        dispatch({ type: "FETCH_ERROR", error });
        return { ok: false, error };
      }
      return { ok: true };
    }
    dispatch({ type: "OPTIMISTIC_REMOVE_LINE", lineId });
    try {
      const cart = await embedApiFetch<Cart>(
        `/api/v1/marketplace/me/cart/lines/${lineId}`,
        { method: "DELETE" },
      );
      if (cart) dispatch({ type: "SET_CART", cart });
      return { ok: true };
    } catch (err) {
      // Rollback: re-fetch the real cart. Same rationale as updateLineQty's
      // catch block above — the explicit return value is the only reliable
      // signal when the resync succeeds and clears state.error.
      const msg = err instanceof ApiError ? err.message : "Failed to remove item";
      try {
        const cart = await embedApiFetch<Cart>("/api/v1/marketplace/me/cart");
        dispatch({ type: "FETCH_SUCCESS", cart: cart ?? EMPTY_CART });
      } catch {
        dispatch({ type: "FETCH_ERROR", error: msg });
      }
      return { ok: false, error: msg };
    }
  }, [token]);

  const setLineStore = useCallback(async (lineId: string, storeId: string, quantity: number): Promise<AddLineResult> => {
    if (!token) {
      // A guest line has no server row to PATCH — the signed-out flow STAGES
      // a draft (store-staging.ts) and reconciles after login (design §6.2).
      return { ok: false };
    }
    // Deliberately NO optimistic dispatch: a store association changes no
    // money, and the response's SET_CART below is the authoritative re-seed
    // (rule 59's spirit — local state derives from the mutation response).
    try {
      const cart = await embedApiFetch<Cart>(
        `/api/v1/marketplace/me/cart/lines/${lineId}`,
        { method: "PATCH", body: { quantity, store_id: storeId }, headers: idempotencyHeaders() },
      );
      if (cart) dispatch({ type: "SET_CART", cart });
      return { ok: true };
    } catch (err) {
      // Rollback: re-fetch the real cart, mirroring updateLineQty's catch —
      // and like there, the explicit return value is the only reliable
      // failure signal when the resync succeeds (it clears state.error).
      const msg = err instanceof ApiError ? err.message : "Failed to save store choice";
      try {
        const cart = await embedApiFetch<Cart>("/api/v1/marketplace/me/cart");
        dispatch({ type: "FETCH_SUCCESS", cart: cart ?? EMPTY_CART });
      } catch {
        dispatch({ type: "FETCH_ERROR", error: msg });
      }
      return { ok: false, error: msg };
    }
  }, [token]);

  const applyPromo = useCallback(async (code: string): Promise<Cart> => {
    const cart = await embedApiFetch<Cart>("/api/v1/marketplace/me/cart/promo", {
      method: "PUT",
      body: { code },
      headers: idempotencyHeaders(),
    });
    const result = cart ?? EMPTY_CART;
    dispatch({ type: "SET_CART", cart: result });
    return result;
  }, []);

  const clearPromo = useCallback(async () => {
    const cart = await embedApiFetch<Cart>("/api/v1/marketplace/me/cart/promo", {
      method: "DELETE",
    });
    if (cart) dispatch({ type: "SET_CART", cart });
  }, []);

  const clearCart = useCallback(() => {
    // Called on checkout completion and on sign-out — both are §6.1 clear
    // triggers for the staging buffer as well as for local cart state.
    clearGuestCart();
    // Invalidate any in-flight fetchCart so its FETCH_SUCCESS cannot land
    // after CLEAR and resurrect the just-cleared cart (TD-314).
    fetchGenRef.current++;
    dispatch({ type: "CLEAR" });
  }, []);

  /**
   * Replay the staged guest buffer into the real cart, then reconcile (§6.3).
   *
   * Lines are replayed SEQUENTIALLY and INDEPENDENTLY: ordering stays
   * deterministic, concurrent writes to one cart row are avoided, and a
   * product deactivated between staging and sign-in fails only its own line.
   *
   * The buffer is CLAIMED up front — cleared unconditionally before the first
   * POST, not after the loop. That is deliberate: `UpsertLineInTx` runs
   * `SET quantity = quantity + $1` (cart_pgx.go:398-410), so replaying a buffer
   * twice DOUBLES the customer's quantities, and the replay guard is a ref that
   * only covers one provider instance. Retaining failed entries for a later
   * retry is not safe either — a POST that times out AFTER the server committed
   * would be re-sent with a fresh Idempotency-Key and double-add. Losing a line
   * the customer can re-add beats silently doubling a line they cannot see, and
   * claiming up front applies that same rule to the interruption case.
   */
  const replayGuestCart = useCallback(async () => {
    const staged = readStagedEntries();
    if (staged.length === 0) {
      await fetchCart();
      return;
    }

    // CLAIM the buffer here — synchronously, before the first await, exactly
    // like the replayedRef flip. The ref only guards ONE provider instance, but
    // the buffer is the shared durable resource every instance reads, so the
    // claim is what actually serialises them:
    //   - index.tsx mounts one React root per [id^="aldelo-storefront-"]
    //     container synchronously in a single querySelectorAll().forEach() pass,
    //     each with its own AuthProvider+CartProvider, and AuthProvider hydrates
    //     the JWT in its useState initializer — so on a two-container page both
    //     replay effects run in the same task, before ANY POST settles. Writing
    //     the remaining tail after each settle is far too late: both instances
    //     have already snapshotted the same buffer and will POST every line
    //     twice, doubling each via `SET quantity = quantity + $1`.
    //   - A page reload mid-replay is the same story with the instances
    //     separated in time rather than space.
    // Claiming before the first suspension point means a concurrent or later
    // instance reads [] and replays nothing.
    clearGuestCart();

    const failed: string[] = [];
    for (let i = 0; i < staged.length; i++) {
      const entry = staged[i];
      try {
        // The staged DELTA, not a client-computed merge — the server owns the
        // arithmetic, so a returning customer's existing quantity is summed.
        await embedApiFetch<Cart>("/api/v1/marketplace/me/cart/lines", {
          method: "POST",
          body: { product_id: entry.product_id, quantity: entry.quantity },
          headers: idempotencyHeaders(),
        });
      } catch (err) {
        // A 401 is categorically different from every other failure: the server
        // rejected the request BEFORE committing anything, and the token is now
        // known bad, so every remaining POST would fail identically. Un-claiming
        // what was never committed therefore cannot double-add — the hazard the
        // up-front claim exists to prevent — while claiming it anyway would
        // destroy the customer's cart outright (buffer gone, nothing on the
        // server). Restore the uncommitted tail and stop.
        if (err instanceof ApiError && err.isUnauthenticated) {
          const notCommitted = staged.slice(i);
          // Suppress the buffer-clear in the sign-out branch of the token
          // effect below: this 401 calls onUnauthenticated -> clearToken, and
          // that INVOLUNTARY sign-out must not delete the cart we just chose to
          // preserve. A deliberate sign-out still clears, as it must.
          preserveBufferOnSignOutRef.current = true;
          writeGuestCart(collapseGuestEntries([...readGuestCart(), ...notCommitted]));
          return;
        }
        failed.push(entry.product_id);
      }
      // Nothing is written back during the loop. Re-persisting a tail here
      // would re-open the same-tick window (a concurrent instance would
      // snapshot that tail and replay it), and it would also resurrect a buffer
      // that a sign-out during replay had just cleared.
    }

    await fetchCart();

    if (failed.length > 0) {
      // FETCH_ERROR preserves state.cart, so the lines that DID replay stay
      // visible beneath a non-blocking message.
      dispatch({
        type: "FETCH_ERROR",
        error: `${failed.length} item${failed.length > 1 ? "s are" : " is"} no longer available and could not be added.`,
      });
    }
  }, [fetchCart]);

  /**
   * Replay-at-most-once guard. A ref, not state: it must be readable and
   * writable SYNCHRONOUSLY in the same block, with no await between the read
   * and the write, so no second entry can observe the pre-write value. State
   * would not have committed before a StrictMode double-invoke re-ran the
   * effect body.
   */
  const replayedRef = useRef(false);
  /**
   * Whether a token has ever been observed by this provider. Distinguishes the
   * two null-token cases, which require OPPOSITE handling: a mount with no
   * token must HYDRATE the buffer (§6.2 — sessionStorage survives a page
   * refresh, so a guest who staged items and reloaded must see them again),
   * while an actual sign-out must CLEAR it (§6.1).
   */
  const hadTokenRef = useRef(false);
  /**
   * Set by replayGuestCart when a 401 forced it to restore the uncommitted
   * buffer. Consumed exactly once by the sign-out branch below, which would
   * otherwise delete that buffer immediately — the 401 itself triggers
   * clearToken(), so the restore and the involuntary sign-out always arrive
   * together. Only the INVOLUNTARY case is suppressed; a deliberate sign-out
   * finds the flag false and clears as before.
   */
  const preserveBufferOnSignOutRef = useRef(false);

  useEffect(() => {
    if (token) {
      hadTokenRef.current = true;
      if (replayedRef.current) {
        // Token changed while already signed in (e.g. a future refresh path).
        // Reconcile, but NEVER replay again — the buffer is already gone and a
        // second replay would double quantities.
        void fetchCart();
        return;
      }
      // Flip BEFORE invoking replay. replayGuestCart runs synchronously up to
      // its first await, so the guard covers the whole in-flight window, not
      // just the window after the network settles.
      replayedRef.current = true;
      void replayGuestCart();
      return;
    }

    const wasSignedIn = hadTokenRef.current;
    hadTokenRef.current = false;
    // Re-arm: a new buffer staged after sign-out is a NEW buffer and must be
    // allowed to replay on the next sign-in.
    replayedRef.current = false;
    // Invalidate any in-flight fetchCart from the just-ended authenticated
    // session so its FETCH_SUCCESS cannot resurrect the cleared cart (TD-314).
    fetchGenRef.current++;
    dispatch({ type: "CLEAR" });

    if (wasSignedIn) {
      if (preserveBufferOnSignOutRef.current) {
        // Involuntary sign-out from a replay 401 (see the ref's doc comment).
        // The buffer was deliberately restored and must survive; render it so
        // the customer sees the cart they still have rather than an empty one
        // that silently refills on the next reload.
        preserveBufferOnSignOutRef.current = false;
        dispatch({ type: "SET_GUEST_LINES", entries: readStagedEntries() });
        return;
      }
      clearGuestCart();
      return;
    }

    // Dispatch unconditionally, even when the buffer is empty (Task 17
    // review round 1 finding). The SET_GUEST_LINES reducer branch builds a
    // valid, correctly-shaped empty cart from `[]` by construction
    // ({...EMPTY_CART, lines: []}), so this is correct for the empty case,
    // and it is the ONLY thing that takes a fresh guest with nothing
    // staged from {status:"idle", cart:null} (the CLEAR dispatch above,
    // which is otherwise permanent for that visitor) to {status:"ready",
    // cart:{...}}. Guarding on `staged.length > 0` left that visitor
    // stuck at "idle" forever — CartView's showEmptyCart used to paper
    // over the symptom by treating "idle" as "safe to render empty", but
    // that produced a NEW bug (idle is also INITIAL_STATE for an
    // authenticated mount whose cart hasn't loaded yet). Closing the gap
    // here, at the producer, means every consumer that gates on
    // `status === "ready"` — not just CartView — gets a real ready state.
    const staged = readStagedEntries();
    dispatch({ type: "SET_GUEST_LINES", entries: staged });
  }, [token, replayGuestCart, fetchCart]);

  const itemCount = useMemo(
    () => state.cart?.lines.reduce((sum, l) => sum + l.quantity, 0) ?? 0,
    [state.cart],
  );

  const value = useMemo<CartContextValue>(
    () => ({
      state,
      isGuest: !token,
      itemCount,
      fetchCart,
      addLine,
      updateLineQty,
      removeLine,
      setLineStore,
      applyPromo,
      clearPromo,
      clearCart,
    }),
    [state, token, itemCount, fetchCart, addLine, updateLineQty, removeLine, setLineStore, applyPromo, clearPromo, clearCart],
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

/** Hook to access cart state and actions. Must be inside CartProvider. */
export function useCart(): CartContextValue {
  const ctx = useContext(CartContext);
  if (!ctx) {
    throw new Error("useCart must be used within a CartProvider");
  }
  return ctx;
}
